import { DALI_BRIDGE_ADDRESS, DALI_MASTER } from '../catalog/dali';
import { ISSUE, ISSUE_TARGET, configIssue } from '../catalog/issue';
import type { ConfigIssue } from '../catalog/issue';
import { RCU_LIMITS } from '../catalog/limits';
import type { DaliBridgeId } from '../ids';
import { newDaliBridgeId } from '../ids';
import type { CommandResult, DaliBridge, RcuConfig } from '../model';
import { moduleCount } from '../query/capacity';
import { findDaliBridge } from '../query/lookup';
import { fail } from '../result';
import { changedBy } from './outcome';

export interface DaliBridgeDraft {
  /** Rotary address of the module, 0–15. */
  readonly address: number;
  /** DALI bus the bridge owns, 0–15. */
  readonly master: number;
  readonly label: string;
}

export interface DaliBridgePatch {
  readonly address?: number;
  readonly master?: number;
  readonly label?: string;
}

const bridgeNotFound = (id: DaliBridgeId): ConfigIssue =>
  configIssue(ISSUE.DALI_BRIDGE_NOT_FOUND, { kind: ISSUE_TARGET.BRIDGE, id }, id);

/**
 * Problems with a bridge's address and master bus, ignoring `ignoreId` so a
 * bridge being edited does not conflict with itself.
 *
 * A bridge answers on one DALI bus, so two bridges on the same master would
 * fight over the same fixtures — that is refused rather than warned about.
 */
const bridgeShapeIssue = (
  config: RcuConfig,
  draft: DaliBridgeDraft,
  ignoreId: DaliBridgeId | null,
): ConfigIssue | null => {
  const siblings = config.daliBridges.filter((bridge) => bridge.id !== ignoreId);
  const target = { kind: ISSUE_TARGET.BRIDGE } as const;

  if (
    draft.address < DALI_BRIDGE_ADDRESS.MIN ||
    draft.address > DALI_BRIDGE_ADDRESS.MAX
  ) {
    return configIssue(ISSUE.RAIL_ADDRESS_OUT_OF_RANGE, target, String(draft.address));
  }
  if (draft.master < DALI_MASTER.MIN || draft.master > DALI_MASTER.MAX) {
    return configIssue(ISSUE.COMMAND_FIELD_OUT_OF_RANGE, target, `Master ${draft.master}`);
  }
  if (siblings.some((bridge) => bridge.address === draft.address)) {
    return configIssue(ISSUE.DALI_BRIDGE_ADDRESS_CONFLICT, target, String(draft.address));
  }
  if (siblings.some((bridge) => bridge.master === draft.master)) {
    return configIssue(ISSUE.DALI_BRIDGE_MASTER_CONFLICT, target, String(draft.master));
  }
  return null;
};

export const addDaliBridge = (
  config: RcuConfig,
  draft: DaliBridgeDraft,
  now: string,
): CommandResult<DaliBridgeId> => {
  if (config.daliBridges.length >= RCU_LIMITS.MAX_DALI_BRIDGES) {
    return fail(
      configIssue(
        ISSUE.DALI_BRIDGE_LIMIT,
        { kind: ISSUE_TARGET.BRIDGE },
        String(config.daliBridges.length),
      ),
    );
  }
  if (moduleCount(config) >= RCU_LIMITS.MAX_MODULES_TOTAL) {
    return fail(
      configIssue(ISSUE.MODULE_TOTAL_LIMIT, { kind: ISSUE_TARGET.BRIDGE }, String(moduleCount(config))),
    );
  }

  const shapeIssue = bridgeShapeIssue(config, draft, null);
  if (shapeIssue !== null) return fail(shapeIssue);

  const bridge: DaliBridge = {
    id: newDaliBridgeId(),
    address: draft.address,
    master: draft.master,
    label: draft.label,
  };
  return changedBy({ ...config, daliBridges: [...config.daliBridges, bridge] }, bridge.id, now);
};

export const updateDaliBridge = (
  config: RcuConfig,
  id: DaliBridgeId,
  patch: DaliBridgePatch,
  now: string,
): CommandResult<DaliBridgeId> => {
  const bridge = findDaliBridge(config, id);
  if (bridge === undefined) return fail(bridgeNotFound(id));

  const next: DaliBridge = { ...bridge, ...patch };
  const shapeIssue = bridgeShapeIssue(config, next, id);
  if (shapeIssue !== null) return fail(shapeIssue);

  return changedBy(
    {
      ...config,
      daliBridges: config.daliBridges.map((current) => (current.id === id ? next : current)),
    },
    id,
    now,
  );
};

/**
 * Removes a bridge. Nothing guest-facing is wired to it — DALI fixtures are
 * addressed through the bridge, not bound as loads — so nothing else changes.
 */
export const removeDaliBridge = (
  config: RcuConfig,
  id: DaliBridgeId,
  now: string,
): CommandResult<number> => {
  const bridge = findDaliBridge(config, id);
  if (bridge === undefined) return fail(bridgeNotFound(id));

  return changedBy(
    { ...config, daliBridges: config.daliBridges.filter((current) => current.id !== id) },
    1,
    now,
  );
};
